import { useState, useRef, useEffect } from "react";
import styles from "./ProductCarousel.module.scss";
import { gsap } from "gsap";
import Buttons from "components/Buttons";
import { useDrag } from "@use-gesture/react";
import cx from 'classnames';
import { withTranslation } from 'react-i18next';

import { useRecoilValue } from 'recoil';
import { isMobileAtom } from 'store/atoms';

import Config from 'config/config.json';
import {getLocaleTag} from 'services/i18n.js';

const { contentfulTranslationsPath, contentfulAccessToken } = Config;

const sliderGalleryQuery = `
  items {
    header,
    mobileHeader,
    image {
      url
    },
    sub,
    mobileSub,
    cta,
    ctaMobile,
    ctaLink,
    noteCollection(limit: 4) {
      items {
        position,
        note,
        icon {
          url
        }
      }
    }
  }
`

const CarouselItem = ({item, gender, index}) => {
  const isMobile = useRecoilValue(isMobileAtom);
  const {image, header, mobileHeader, mobileSub, sub, cta, ctaMobile, ctaLink, noteCollection} = item;
  const notes = noteCollection.items;

  return (
    <li className={styles.slider_Item}>
      <div className={[styles.content, styles.itemsTop].join(' ')}>
        <div className={styles.slideInfo}>
          <h3>{isMobile ? mobileHeader : header}</h3>
          <p>{isMobile ? mobileSub : sub}</p>
          {(cta || ctaMobile) && 
            <Buttons
              data-analytics 
              data-action="click_cta" 
              data-label={`click_product${gender}${index}`} 
              data-category="ecommerce"
              href={ctaLink}
              width="225px"
              color="#000"
              background="#fff"
              border="1px solid #fff">
              {isMobile ? (ctaMobile || cta) : (cta || ctaMobile)}
            </Buttons>
          }
        </div>
      </div>
      <div className={[styles.content, styles.itemsTop].join(' ')}>

        <div className={styles.SliderimgOverlay}>
          {notes.map(({note, icon, position}) =>
            <div className={cx(styles.info_Item, {
              [styles.info_Item_top_right]: position === 'Top Right',
              [styles.info_Item_top_left]: position === 'Top Left',
              [styles.info_Item_bottom_right]: position === 'Bottom Right',
              [styles.info_Item_bottom_left]: position === 'Bottom Left'
            })}>
              <div className={[styles.pointer_Item, styles.pointer_Top].join(' ')}>
                <img src={icon?.url} alt="" />
              </div>
              <p>{note}</p>
              <div className={[styles.pointer_Item, styles.pointer_Left].join(' ')}>
                <span></span>
              </div>
            </div>
          )}
        </div>
        
        <img
          alt={isMobile ? mobileHeader : header}
          className={styles.image_one}
          src={image?.url}
        />
      </div>

      <div className={[styles.content, styles.itemsBottom].join(' ')}>
      <div className={styles.SliderimgOverlay}></div>

        <div className={styles.SliderimgOverlay}>
          {notes.map(({note, icon, position}) =>
            <div className={cx(styles.info_Item, {
              [styles.info_Item_top_right]: position === 'Top Right',
              [styles.info_Item_top_left]: position === 'Top Left',
              [styles.info_Item_bottom_right]: position === 'Bottom Right',
              [styles.info_Item_bottom_left]: position === 'Bottom Left'
            })}>
              <div className={[styles.pointer_Item, styles.pointer_Top].join(' ')}>
                <img src={icon?.url} alt="" />
              </div>
              <p>{note}</p>
              <div className={[styles.pointer_Item, styles.pointer_Left].join(' ')}>
                <span></span>
              </div>
            </div>
          )}
        </div>
        <img
          alt={isMobile ? mobileHeader : header}
          className={styles.image_one}
          src={image?.url}
        />
      </div>
      <div className={[styles.content, styles.itemsBottom].join(' ')}>
        <div className={styles.slideInfo}>
          <h3>{isMobile ? mobileHeader : header}</h3>
          <p>{isMobile ? mobileSub : sub}</p>
          {(cta || ctaMobile) && 
            <Buttons
              data-analytics 
              data-action="click_cta" 
              data-label={`click_product${gender}${index}`} 
              data-category="ecommerce"
              href={ctaLink}
              width="225px"
              color="#000"
              background="#fff"
              border="1px solid #fff">
              {isMobile ? (ctaMobile || cta) : (cta || ctaMobile)}
            </Buttons>
          }
        </div>
      </div>
    </li>
  );
}

const Carousel = ({ data, analyticsKey }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTitle, setCurrentTitle] = useState('');
  const slides = useRef(null);
  const isMobile = useRecoilValue(isMobileAtom);

  useDrag(({ swipe: [swipeX] }) => {
    if (swipeX > 0) goSlideRight();
    if (swipeX < 0) goSlideLeft();
  }, { target: slides })

  const goSlideLeft = () => {
    setCurrentIndex(currentIndex === data.length - 1 ? 0 : currentIndex + 1);

    const sliderTop = slides.current;
    const slideWidth = sliderTop.querySelector("li").offsetWidth;
    const currentValue = sliderTop.getBoundingClientRect().left;

    gsap
      .to([sliderTop], {
        marginLeft: currentValue - slideWidth,
        duration: 0.5,
      })
      .then(() => {
        sliderTop
          .querySelector("li:nth-last-of-type(1)")
          .after(sliderTop.querySelector("li:nth-child(1)"));
        sliderTop.style.marginLeft = 0;
      });

      window.TTB?.onAnalyticsEvent("click_carousel", `click_carousel${analyticsKey}right`, "engagement");
  };

  const goSlideRight = () => {
    setCurrentIndex(currentIndex === 0 ? data.length - 1 : currentIndex - 1);

    const sliderTop = slides.current;
    const slideWidth = sliderTop.querySelector("li").offsetWidth;

    sliderTop
      .querySelector("li:nth-child(1)")
      .before(sliderTop.querySelector("li:nth-last-of-type(1)"));
    sliderTop.style.marginLeft = `-${slideWidth}px`;

    gsap.to([sliderTop], {
      marginLeft: 0,
      duration: 0.5,
    });

    window.TTB?.onAnalyticsEvent("click_carousel", `click_carousel${analyticsKey}left`, "engagement");
  };

  useEffect(() => {
    if(data[currentIndex]){
      const itemData = data[currentIndex];
      setCurrentTitle(isMobile ? itemData.mobileHeader : itemData.header);
    }
  }, [currentIndex, data, isMobile]);

  return (
    <>
      <div className={styles.overlay_Scroll}>
        <div className={styles.slider}>
          <span
            className={[styles.controls, styles.contolleft].join(" ")}
            onClick={() => {
              goSlideRight();
            }}
          ></span>
          <span
            className={[styles.controls, styles.contolright].join(" ")}
            onClick={() => {
              goSlideLeft();
            }}
          ></span>
          <ul className={styles.slides} ref={slides}>
            {data.map((item, index) => <CarouselItem item={item} gender={analyticsKey} index={index}/>)}
            <div className={styles.bullets} id="dotsTop">
              <div className={styles.bullet_content}>
                {Array.apply(null, { length: data.length }).map((e, i) => (
                  <label
                    key={i}
                    className={styles.bullet_item}
                    style={{
                      background: currentIndex === i ? "#fff" : "none",
                    }}
                  ></label>
                ))}
              </div>
            </div>
          </ul>
        </div>
      </div>
      <div className={[styles.m_scroll, styles.top].join(" ")}>
        <div className={styles.m_scroll__title}>
          <div className={styles.rollTop}>
            {Array.apply(null, { length: 3 }).map((e, i) => (
              <h2>
                <span className={styles.T_product}>{currentTitle}</span>&nbsp;
              </h2>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const ProductCarousel = ({t}) => {
  const [firstItems, setFirstItems] = useState([]);
  const [secItems, setSecItems] = useState([]);
  const [thirdItems, setThirdItems] = useState([]);
  const [ctaData, setCtaData] = useState({});

  useEffect(() => {
    fetch(contentfulTranslationsPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${contentfulAccessToken}`,
      },
      body: JSON.stringify({ query: `
        {
          productGalleryDrop2Collection(limit:1, where: {contentfulMetadata: {
            tags: {
              id_contains_all: "${getLocaleTag()}"
            }
          }}) {
            items {
              slider1ItemsCollection {
                ${sliderGalleryQuery}
              },
              slider2ItemsCollection {
                ${sliderGalleryQuery}
              },
              slider3ItemsCollection {
                ${sliderGalleryQuery}
              },
              cta1,
              cta1Link,
              cta2,
              cta2Link
            }
          }
        }
      ` }),
    }).then(res => res.json())
    .then(data => {
      const gallery = data?.data?.productGalleryDrop2Collection?.items[0];
      if(!gallery){
        return;
      }
      const {
        slider1ItemsCollection, 
        slider2ItemsCollection, 
        slider3ItemsCollection
      } = gallery;
      setFirstItems(slider1ItemsCollection.items);
      setSecItems(slider2ItemsCollection.items);
      setThirdItems(slider3ItemsCollection.items);
      setCtaData({
        cta1: gallery.cta1,
        cta1Link: gallery.cta1Link,
        cta2: gallery.cta2,
        cta2Link: gallery.cta2Link,
      });
    });
  }, []);


  const {cta2Link, cta2, cta1, cta1Link} = ctaData;

  return (
    <div className={styles.ProductCarousel}>
      <div className={styles.CarouselTitle}>
        <h2 dangerouslySetInnerHTML={{__html: t('drop2_module5_title')}}/>
      </div>
      <div className={styles.brandtopSlide}>
        <Carousel analyticsKey="men" data={firstItems} />
      </div>
      <div className={styles.brandmiddleSlide}>
        <Carousel analyticsKey="wmn" data={secItems} />
      </div>
      <div className={styles.brandbottomSlide}>
        <Carousel analyticsKey="dual" data={thirdItems} />
      </div>
      <Buttons
        data-analytics 
        data-action="click_cta" 
        data-label="click_shopwmn" 
        data-category="ecommerce"
        href={cta1Link}
        width="225px"
        color="#000"
        background="#fff"
        border="1px solid #fff">
        {cta1}
      </Buttons>
      <Buttons
       data-analytics 
       data-action="click_cta" 
       data-label="click_shopmen" 
       data-category="ecommerce"
        href={cta2Link}
        width="225px"
        color="#000"
        background="#fff"
        border="1px solid #fff">
        {cta2}
      </Buttons>
    </div>
  );
};

export default withTranslation()(ProductCarousel);